'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import { useTexture } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

import { fragmentShaderOptimized } from './shaders/fragmentOptimized';
import { vertexShader } from './shaders/vertex';
import { simplex2D } from './utils/noise';

// --- SHARED NOISE GENERATION (Module Level) ---
const LUT_SIZE = 256;
const LUT_MASK = LUT_SIZE - 1;
const NOISE_LUT = new Float32Array(LUT_SIZE * LUT_SIZE);

// Populate with noise once
for (let i = 0; i < LUT_SIZE * LUT_SIZE; i += 1) {
  const x = i % LUT_SIZE;
  const y = Math.floor(i / LUT_SIZE);

  // Scale 0.05 determines the "tightness" of the noise pattern in the texture
  NOISE_LUT[i] = simplex2D(x * 0.05, y * 0.05);
}
// ----------------------------------------------

// Fast inverse square root (Quake III algorithm)
function fastInvSqrt(x: number): number {
  const halfx = 0.5 * x;
  const buf = new ArrayBuffer(4);
  const floatView = new Float32Array(buf);
  const intView = new Uint32Array(buf);

  floatView[0] = x;
  // eslint-disable-next-line no-bitwise
  intView[0] = 0x5f3759df - (intView[0] >> 1);

  let y = floatView[0];

  y *= 1.5 - halfx * y * y;

  return y;
}

export interface OptimizedDitherImageProps {
  src: string;
  density?: number;
  size?: number;
  blackCutoff?: number;
  fadeInDuration?: number;
  lerpStrength?: number;
  repelRadius?: number;
  repelStrength?: number;
  returnStrength?: number;
  friction?: number;
  interactionEnabled?: boolean;
  noiseEnabled?: boolean;
  noiseScale?: number;
  noiseSpeed?: number;
  noiseAmplitude?: number;
  brightnessFlickerEnabled?: boolean;
  brightnessFlickerScale?: number;
  brightnessFlickerSpeed?: number;
  brightnessFlickerAmount?: number;
  grayscale?: boolean;
  contrast?: number;
  brightness?: number;
  ditherEnabled?: boolean;
  ditherScale?: number;
  objectFit?: 'cover' | 'contain';
}

const OptimizedDitherImage = ({
  src,
  density = 150,
  size: particleSize = 4.0,
  blackCutoff = 0.1,
  fadeInDuration = 0.8,
  lerpStrength = 0.4,
  repelRadius = 1.5,
  repelStrength = 0.01,
  returnStrength = 0.05,
  friction = 0.8,
  interactionEnabled = true,
  noiseEnabled = true,
  noiseScale = 100.0,
  noiseSpeed = 0.2,
  noiseAmplitude = 0.001,
  brightnessFlickerEnabled = true,
  brightnessFlickerScale = 5.0,
  brightnessFlickerSpeed = 0.03,
  brightnessFlickerAmount = 1.0,
  grayscale = true,
  contrast = 1.2,
  brightness = 1.0,
  ditherEnabled = true,
  ditherScale = 1.0,
  objectFit = 'cover',
}: OptimizedDitherImageProps) => {
  const texture = useTexture(src);
  const { gl, viewport, size } = useThree();
  const isHoveringRef = useRef(false);
  const materialRef = useRef<THREE.ShaderMaterial>(null);
  const geometryRef = useRef<THREE.BufferGeometry>(null);
  const opacityRef = useRef(0);
  const flickerTimeRef = useRef(0);
  const noiseTimeRef = useRef(0);

  // Debounce viewport changes to avoid expensive recalculations on every resize event
  const [debouncedSize, setDebouncedSize] = useState({ width: 0, height: 0 });

  // Initialize on mount with actual dimensions
  useEffect(() => {
    setDebouncedSize({ width: size.width, height: size.height });
  }, [size.width, size.height]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSize({ width: size.width, height: size.height });
    }, 300);

    return () => clearTimeout(timer);
  }, [size.width, size.height]);

  const positionsRef = useRef<Float32Array | null>(null);
  const originalPositionsRef = useRef<Float32Array | null>(null);
  const velocitiesRef = useRef<Float32Array | null>(null);
  const particleCountRef = useRef(0);

  const mouseRef = useRef({ x: 9999, y: 9999 });
  const targetMouseRef = useRef({ x: 9999, y: 9999 });
  const mouseMovedRef = useRef(false);
  const lastMouseUpdateRef = useRef(0);

  // Visibility detection for performance optimization
  const isVisibleRef = useRef(true);

  // Cache bounding rect to avoid forced reflows
  const cachedRectRef = useRef<DOMRect | null>(null);

  // Detect mobile once at mount for fast path
  const isMobileRef = useRef(typeof window !== 'undefined' && 'ontouchstart' in window);

  const { width, height } = texture.image as HTMLImageElement;
  const aspect = width / height;

  const { positions, originalPositions, uvs, count } = useMemo(() => {
    // Skip calculation if size not initialized yet
    if (debouncedSize.width === 0 || debouncedSize.height === 0) {
      return {
        positions: new Float32Array(0),
        originalPositions: new Float32Array(0),
        uvs: new Float32Array(0),
        count: 0,
      };
    }

    // Object-fit logic
    const imageAspect = width / height;
    const viewportAspect = viewport.width / viewport.height;

    let planeWidth: number;
    let planeHeight: number;

    switch (objectFit) {
      case 'cover':
        if (imageAspect > viewportAspect) {
          planeHeight = viewport.height;
          planeWidth = planeHeight * imageAspect;
        } else {
          planeWidth = viewport.width;
          planeHeight = planeWidth / imageAspect;
        }

        break;
      default:
        if (imageAspect > viewportAspect) {
          planeWidth = viewport.width;
          planeHeight = planeWidth / imageAspect;
        } else {
          planeHeight = viewport.height;
          planeWidth = planeHeight * imageAspect;
        }

        break;
    }

    // Dynamic density based on SCREEN SIZE (pixels) not world units
    // Only scale DOWN on smaller screens, never scale UP beyond input density
    const screenWidth = debouncedSize.width;
    const viewportScale = screenWidth > 0 ? Math.min(1, screenWidth / 720) : 1;
    const deviceScale = typeof navigator !== 'undefined' && navigator.hardwareConcurrency < 4 ? 0.7 : 1.0;
    const mobileScale = isMobileRef.current ? 0.6 : 1.0;

    // Calculate effective density - clamp to never exceed input density
    const scaledDensity = density * viewportScale;
    const effectiveDensity = Math.min(density, Math.max(20, Math.floor(scaledDensity)));

    const segmentsX = Math.floor(effectiveDensity * aspect);
    const segmentsY = effectiveDensity;

    const debugInfo = {
      inputDensity: density,
      screenWidth,
      screenHeight: debouncedSize.height,
      viewportScale,
      deviceScale,
      mobileScale,
      isMobile: isMobileRef.current,
      effectiveDensity,
      segmentsX,
      segmentsY,
      expectedParticles: (segmentsX + 1) * (segmentsY + 1),
    };

    const particleCount = (segmentsX + 1) * (segmentsY + 1);

    const tempPositions = new Float32Array(particleCount * 3);
    const tempOriginalPositions = new Float32Array(particleCount * 3);
    const tempUvs = new Float32Array(particleCount * 2);

    let index = 0;

    for (let iy = 0; iy <= segmentsY; iy += 1) {
      const v = iy / segmentsY;
      const y = v * planeHeight - planeHeight / 2;

      for (let ix = 0; ix <= segmentsX; ix += 1) {
        const u = ix / segmentsX;
        const x = u * planeWidth - planeWidth / 2;

        tempPositions[index * 3] = x;
        tempPositions[index * 3 + 1] = y;
        tempPositions[index * 3 + 2] = 0;

        tempOriginalPositions[index * 3] = x;
        tempOriginalPositions[index * 3 + 1] = y;
        tempOriginalPositions[index * 3 + 2] = 0;

        tempUvs[index * 2] = u;
        tempUvs[index * 2 + 1] = v;

        index += 1;
      }
    }

    return {
      positions: tempPositions,
      originalPositions: tempOriginalPositions,
      uvs: tempUvs,
      count: particleCount,
      particleInfo: debugInfo,
    };
  }, [
    objectFit,
    width,
    height,
    aspect,
    debouncedSize.width,
    debouncedSize.height,
    density,
    viewport.width,
    viewport.height,
  ]);

  // Create DataTexture for GPU
  const noiseTexture = useMemo(() => {
    const tex = new THREE.DataTexture(NOISE_LUT, LUT_SIZE, LUT_SIZE, THREE.RedFormat, THREE.FloatType);

    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.minFilter = THREE.LinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.needsUpdate = true;

    return tex;
  }, []);

  // Initialize physics state and geometry attributes imperatively
  useEffect(() => {
    if (!geometryRef.current || count === 0) {
      return undefined;
    }

    const geometry = geometryRef.current;

    // Dispose old attributes before creating new ones
    const oldPosition = geometry.attributes.position;
    const oldUv = geometry.attributes.aOriginalUv;

    if (oldPosition) {
      geometry.deleteAttribute('position');
    }

    if (oldUv) {
      geometry.deleteAttribute('aOriginalUv');
    }

    // Create new arrays and attributes
    positionsRef.current = new Float32Array(positions);
    originalPositionsRef.current = new Float32Array(originalPositions);
    velocitiesRef.current = new Float32Array(count * 3);
    particleCountRef.current = count;

    const positionAttribute = new THREE.BufferAttribute(positionsRef.current, 3);

    geometry.setAttribute('position', positionAttribute);

    const uvAttribute = new THREE.BufferAttribute(uvs, 2);

    geometry.setAttribute('aOriginalUv', uvAttribute);

    // Reset bounding sphere
    geometry.computeBoundingSphere();

    return () => {
      // Cleanup on unmount
      if (geometry.attributes.position) {
        geometry.deleteAttribute('position');
      }

      if (geometry.attributes.aOriginalUv) {
        geometry.deleteAttribute('aOriginalUv');
      }

      positionsRef.current = null;
      originalPositionsRef.current = null;
      velocitiesRef.current = null;
    };
  }, [positions, originalPositions, uvs, count]);

  const uniforms = useMemo(
    () => ({
      uTexture: { value: texture },
      uNoiseTexture: { value: noiseTexture }, // Pass the shared noise texture
      uPointSize: { value: particleSize },
      uPixelRatio: { value: gl.getPixelRatio() },
      uBlackCutoff: { value: blackCutoff },
      uOpacity: { value: 0 },
      uTime: { value: 0 },
      uBrightnessFlickerScale: { value: brightnessFlickerScale },
      uBrightnessFlickerAmount: { value: brightnessFlickerEnabled ? brightnessFlickerAmount : 0 },
      uGrayscale: { value: grayscale ? 1.0 : 0.0 },
      uContrast: { value: contrast },
      uBrightness: { value: brightness },
      uDitherEnabled: { value: ditherEnabled ? 1.0 : 0.0 },
      uDitherScale: { value: ditherScale },
    }),
    [
      texture,
      noiseTexture,
      particleSize,
      gl,
      blackCutoff,
      brightnessFlickerScale,
      brightnessFlickerEnabled,
      brightnessFlickerAmount,
      grayscale,
      contrast,
      brightness,
      ditherEnabled,
      ditherScale,
    ]
  );

  // Intersection Observer to pause rendering when off-screen
  useEffect(() => {
    const canvas = gl.domElement;

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting;
      },
      {
        threshold: 0.1, // Trigger when at least 10% visible
      }
    );

    observer.observe(canvas);

    return () => {
      observer.disconnect();
    };
  }, [gl]);

  // Cache bounding rect and update on resize/scroll to avoid forced reflows
  useEffect(() => {
    const canvas = gl.domElement;

    const updateRect = () => {
      cachedRectRef.current = canvas.getBoundingClientRect();
    };

    // Initial update
    updateRect();

    // Update on resize and scroll (passive for better performance)
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, { passive: true });

    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect);
    };
  }, [gl]);

  // Mouse handling
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!size) return;

      // Use cached rect (updated on scroll/resize) to avoid forced reflows
      const rect = cachedRectRef.current;

      if (!rect) return;

      // Check if mouse is within this canvas bounds
      const isInBounds =
        e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;

      // Update hover state based on bounds
      isHoveringRef.current = isInBounds;

      // Only process mouse position if within bounds
      if (isInBounds) {
        const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

        targetMouseRef.current.x = (x * viewport.width) / 2;
        targetMouseRef.current.y = (y * viewport.height) / 2;
        mouseMovedRef.current = true;
        lastMouseUpdateRef.current = performance.now();
      }
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [size, gl, viewport]);

  // Physics update loop
  useFrame((state, delta) => {
    const positionsData = positionsRef.current;
    const originalPositionsData = originalPositionsRef.current;
    const velocities = velocitiesRef.current;
    const geometry = geometryRef.current;

    if (!positionsData || !originalPositionsData || !velocities || !geometry) return;

    // PERFORMANCE: Skip all updates when canvas is not visible
    if (!isVisibleRef.current) return;

    // Update time for opacity flickering shader
    flickerTimeRef.current += delta * brightnessFlickerSpeed;

    if (materialRef.current) {
      materialRef.current.uniforms.uTime.value = flickerTimeRef.current;
    }

    // Update time for noise displacement
    noiseTimeRef.current += delta * noiseSpeed;

    // Animate opacity fade-in
    if (materialRef.current && opacityRef.current < 1) {
      opacityRef.current = Math.min(1, opacityRef.current + delta / fadeInDuration);
      materialRef.current.uniforms.uOpacity.value = opacityRef.current;
    }

    // EARLY BAILOUT: Skip physics if opacity is too low (particles invisible)
    if (opacityRef.current < 0.01) {
      return;
    }

    // MOBILE FAST PATH: Skip all physics calculations on mobile
    if (isMobileRef.current || !interactionEnabled) {
      return;
    }

    // Decay mouse influence after 2 seconds of inactivity
    const timeSinceMouseMove = performance.now() - lastMouseUpdateRef.current;

    if (timeSinceMouseMove > 2000) {
      mouseMovedRef.current = false;
    }

    // Smooth mouse lerp
    mouseRef.current.x += (targetMouseRef.current.x - mouseRef.current.x) * lerpStrength;
    mouseRef.current.y += (targetMouseRef.current.y - mouseRef.current.y) * lerpStrength;

    const mouseX = mouseRef.current.x;
    const mouseY = mouseRef.current.y;
    const repelRadiusSq = repelRadius * repelRadius;

    // SPATIAL CULLING: Define bounding box around mouse
    const cullingMargin = repelRadius * 2;
    const minX = mouseX - cullingMargin;
    const maxX = mouseX + cullingMargin;
    const minY = mouseY - cullingMargin;
    const maxY = mouseY + cullingMargin;

    // Convert time to a scroll offset
    const timeOffset = Math.floor(noiseTimeRef.current * 20);

    // Physics loop
    for (let i = 0; i < particleCountRef.current; i += 1) {
      const idx = i * 3;

      let x = positionsData[idx];
      let y = positionsData[idx + 1];
      let vx = velocities[idx];
      let vy = velocities[idx + 1];

      const ox = originalPositionsData[idx];
      const oy = originalPositionsData[idx + 1];

      // Optimized Noise Lookup
      if (noiseEnabled) {
        // Calculate index on-the-fly to support dynamic Scale
        // eslint-disable-next-line no-bitwise
        const u = Math.floor(ox * noiseScale + timeOffset) & LUT_MASK;
        // eslint-disable-next-line no-bitwise
        const v = Math.floor(oy * noiseScale + timeOffset) & LUT_MASK;

        // Look up values
        const noiseValX = NOISE_LUT[v * LUT_SIZE + u];

        // Offset Y lookup by 128 to get a different pattern
        // eslint-disable-next-line no-bitwise
        const u2 = (u + 128) & LUT_MASK;
        const noiseValY = NOISE_LUT[v * LUT_SIZE + u2];

        vx += noiseValX * noiseAmplitude;
        vy += noiseValY * noiseAmplitude;
      }

      // SPATIAL CULLING: Only check mouse repel if particle is within bounding box
      if (mouseMovedRef.current && x >= minX && x <= maxX && y >= minY && y <= maxY) {
        const dx = x - mouseX;
        const dy = y - mouseY;
        const distSq = dx * dx + dy * dy;

        if (distSq < repelRadiusSq && distSq > 0.01) {
          // Use fast inverse square root instead of Math.sqrt
          const invDist = fastInvSqrt(distSq);
          const dist = distSq * invDist;
          const force = (1 - dist / repelRadius) * repelStrength;

          vx += dx * invDist * force;
          vy += dy * invDist * force;
        }
      }

      // Spring force (return to original position)
      const returnDx = ox - x;
      const returnDy = oy - y;

      vx += returnDx * returnStrength;
      vy += returnDy * returnStrength;

      // Apply friction
      vx *= friction;
      vy *= friction;

      // Update position
      x += vx;
      y += vy;

      // Store updated values
      positionsData[idx] = x;
      positionsData[idx + 1] = y;
      velocities[idx] = vx;
      velocities[idx + 1] = vy;
    }

    // Upload updated positions to GPU
    const positionAttribute = geometry.attributes.position as THREE.BufferAttribute;

    positionAttribute.needsUpdate = true;
  });

  return (
    <points>
      <bufferGeometry ref={geometryRef} />
      {/* eslint-disable react/no-unknown-property */}
      <shaderMaterial
        ref={materialRef}
        vertexShader={vertexShader}
        fragmentShader={fragmentShaderOptimized}
        uniforms={uniforms}
        transparent
        depthWrite={false}
      />
      {/* eslint-enable react/no-unknown-property */}
    </points>
  );
};

export default OptimizedDitherImage;
